import { observer } from "mobx-react-lite";
import { ReactNode, useCallback } from "react";

import { ModelParameters } from "@/components/ModelParameters";
import { usePlayground } from "@/components/Playground/playground-store";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { PromptBlueprint } from "@/types";

interface MessagesFooterProps {
  leftContent?: ReactNode;
}

const MessagesFooter = observer(({ leftContent }: MessagesFooterProps) => {
  const playground = usePlayground();
  const tour = useOptionalPlaygroundTourContext();

  const handleTourPopoverOpenChange = (open: boolean) => {
    if (!open) {
      return;
    }
    tour?.setModelPopoverOpen(true);
  };

  const handleModeChange = useCallback(
    (isChat: boolean) => {
      playground.setMode(isChat ? "chat" : "completion");
    },
    [playground],
  );

  if (playground.isSnippet) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2 border-t border-gray-200 bg-white px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="flex min-w-0 flex-1 basis-0 items-center justify-start">{leftContent}</div>
      <div className="flex shrink-0 items-center justify-end self-end sm:self-center">
        <ModelParameters
          className="items-center"
          isPromptChat={playground.isChat}
          onModeChange={handleModeChange}
          promptData={playground.blueprint}
          readonly={playground.readonly}
          triggerClassName="h-8 max-w-[240px] justify-start rounded-full px-2.5 text-gray-700 transition-colors hover:bg-gray-100"
          triggerLabelClassName="truncate"
          buttonSize="sm"
          showIcon
          showTooltip={false}
          onUpdate={promptData =>
            playground.applyPromptBlueprintToState(promptData as Partial<PromptBlueprint>, {
              preserveSessionToggles: true,
            })
          }
          controlledOpen={tour?.isActive ? tour.isModelPopoverOpen : undefined}
          onControlledOpenChange={tour?.isActive ? handleTourPopoverOpenChange : undefined}
        />
      </div>
    </div>
  );
});

export default MessagesFooter;
